/**
 * AIIntelligencePage — the unified UI for the €39/mo AI Intelligence Bundle.
 *
 * Four tabs share the same page shell and talk to /api/ai-intel/*:
 *   - Prompt Tracker        (ai_intel_prompts + runs)
 *   - Citation Intelligence (ai_intel_citations)
 *   - Sentiment Analysis    (ai_intel_sentiment)
 *   - Content Gap           (ai_intel_gaps)
 *
 * The page gates itself behind /ai-intel/access; non-entitled users see a
 * single purchase CTA that kicks off the same Stripe checkout flow as every
 * other addon (so admin-bypass and normal Stripe both work automatically).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { ToolHeader, SignInGate } from "./CompetitorSpyPage";
import {
  Brain, Loader2, Plus, Trash2, RefreshCw, Quote, Smile, Target,
  Search, X, Zap, TrendingUp, TrendingDown, CheckCircle2,
} from "lucide-react";
import {
  OrderNowBottomCTA, InlineBuyLink, HeroBuyButton, TrustBarSmall,
} from "../components/common/ServiceOrderBundle";

const TABS = [
  { id: "prompts",  label: "Prompt Tracker",        icon: Search,  testid: "aii-tab-prompts"   },
  { id: "citations",label: "Citation Intelligence", icon: Quote,   testid: "aii-tab-citations" },
  { id: "sentiment",label: "Sentiment Analysis",    icon: Smile,   testid: "aii-tab-sentiment" },
  { id: "gaps",     label: "Content Gap Analyzer",  icon: Target,  testid: "aii-tab-gaps"      },
];

const inputCls = "w-full px-4 py-3 bg-[var(--color-bg)] border border-[var(--color-border)] text-white focus:border-[#a855f7] focus:outline-none";
const btn = "px-5 py-3 bg-[#a855f7] text-white font-bold uppercase hover:bg-purple-600 disabled:opacity-50 flex items-center justify-center gap-2";

export default function AIIntelligencePage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [tab, setTab] = useState("prompts");
  const [hasAccess, setHasAccess] = useState(null);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/ai-intel/access`, { params: { email: user.email } })
      .then((r) => setHasAccess(!!r.data?.has_access))
      .catch(() => setHasAccess(false));
  }, [user]);

  const finalCta = (
    <OrderNowBottomCTA
      packageId="addon_ai_intelligence"
      priceEur={39}
      interval="mes"
      serviceName="AI Intelligence Bundle"
      headline="Odomkni všetky 4 AI dashboardy"
      subcopy="Prompt Tracker, Citation Intelligence, Sentiment Analysis, Content Gap Analyzer — všetko za jednu mesačnú cenu."
      testid="aii-final-cta"
    />
  );

  if (!user) return (
    <SignInGate title={t.aii_title || "AI Intelligence"} sub={t.aii_sign_in || "Sign in to track prompts, citations and AI sentiment."}>
      {finalCta}
    </SignInGate>
  );
  if (hasAccess === null) return <div className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#a855f7]" /></div>;
  if (!hasAccess) return <Paywall />;

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-white">
      <ToolHeader />
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-2">
          <Brain className="w-6 h-6 text-[#a855f7]" />
          <h1 className="text-3xl font-black font-['Cabinet_Grotesk'] tracking-tight" data-testid="aii-title">AI Intelligence</h1>
          <span className="px-2 py-0.5 bg-[#a855f7]/15 text-[#c084fc] text-xs font-bold">€39/mo</span>
        </div>
        <p className="text-[var(--color-text-muted)] mb-6">{t.aii_intro || "Track prompts, citations, sentiment and content gaps across every major AI search engine."}</p>

        <div className="flex flex-wrap gap-2 mb-8 border-b border-[var(--color-border)]">
          {TABS.map((T) => {
            const active = T.id === tab;
            return (
              <button key={T.id} onClick={() => setTab(T.id)} data-testid={T.testid}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 transition-colors ${active ? "border-[#a855f7] text-white" : "border-transparent text-[var(--color-text-muted)] hover:text-white"}`}>
                <T.icon className="w-4 h-4" /> {T.label}
              </button>
            );
          })}
        </div>

        {tab === "prompts"   && <PromptTracker  email={user.email} language={language} />}
        {tab === "citations" && <CitationsTab   email={user.email} language={language} />}
        {tab === "sentiment" && <SentimentTab   email={user.email} language={language} />}
        {tab === "gaps"      && <GapsTab        email={user.email} language={language} />}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Paywall — shown when the user does not own the addon yet. Triggers the
 * exact same Stripe checkout flow the other addons use.
 * ------------------------------------------------------------------------- */
function Paywall() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const go = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        package_id: "addon_ai_intelligence",
        user_email: user.email,
        language: language || "en",
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-white">
      <ToolHeader />
      <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="aii-paywall">
        <Brain className="w-16 h-16 text-[#a855f7] mx-auto mb-6" />
        <h1 className="text-4xl font-black font-['Cabinet_Grotesk'] tracking-tight mb-3">
          {t.aii_pw_title || "Unlock AI Intelligence Bundle"}
        </h1>
        <p className="text-[var(--color-text-muted)] text-lg mb-8">
          {t.aii_pw_sub || "Prompt Tracker · Citation Intelligence · Sentiment · Content Gap — all powered by 5 AI engines."}
        </p>
        <div className="grid sm:grid-cols-2 gap-3 text-left mb-10">
          {[
            "Track 10 prompts × 5 AI engines",
            "Find which URLs AI cites about your brand",
            "Positive / neutral / negative AI sentiment",
            "Discover content topics you miss",
          ].map((f, i) => (
            <div key={i} className="flex items-start gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-[#c084fc] mt-0.5 flex-shrink-0" /><span>{f}</span>
            </div>
          ))}
        </div>
        <button onClick={go} disabled={loading} data-testid="aii-paywall-buy" className={btn + " mx-auto text-lg"}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
          {t.aii_pw_cta || "Unlock for €39/mo"}
        </button>
        <TrustBarSmall testid="aii-paywall-trust" />
        <p className="text-xs text-[var(--color-text-muted)] mt-4">{t.aii_pw_note || "Compare: Profound $99, Peec €89, Writesonic $249."}</p>

        {/* Secondary mid-page CTA */}
        <div className="mt-10 pt-8 border-t border-white/10">
          <p className="text-sm text-[var(--color-text-muted)] mb-3">Alebo rovno Stripe Checkout:</p>
          <InlineBuyLink
            packageId="addon_ai_intelligence"
            priceEur={39}
            interval="mes"
            testid="aii-inline-buy"
          />
        </div>

        {/* Big final CTA */}
        {finalCta}
      </div>
    </div>
  );
}

/* =========================================================================
 * 1) Prompt Tracker
 * ========================================================================= */
function PromptTracker({ email, language }) {
  const [trackers, setTrackers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [latest, setLatest] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ brand: "", promptsText: "" });
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const reload = async () => {
    const r = await axios.get(`${API}/ai-intel/prompts`, { params: { email } });
    setTrackers(r.data?.trackers || []);
  };
  useEffect(() => { reload(); }, [email]);  // eslint-disable-line
  const loadLatest = async (id) => {
    const r = await axios.get(`${API}/ai-intel/prompts/${id}/latest`).catch(() => ({ data: null }));
    setLatest(r.data && !r.data.error ? r.data : null);
  };
  useEffect(() => { if (selected) loadLatest(selected.id); }, [selected]);  // eslint-disable-line

  const create = async (e) => {
    e.preventDefault();
    const prompts = form.promptsText.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 10);
    if (!form.brand || !prompts.length) return;
    setLoading(true);
    const r = await axios.post(`${API}/ai-intel/prompts`, { email, brand: form.brand, prompts, language });
    setShowForm(false);
    setForm({ brand: "", promptsText: "" });
    await reload();
    setSelected(r.data.tracker);
    setLatest(r.data.first_run);
    setLoading(false);
  };
  const rerun = async (id) => {
    setRunning(true);
    const r = await axios.post(`${API}/ai-intel/prompts/${id}/run`);
    setLatest(r.data);
    setRunning(false);
  };
  const remove = async (id) => {
    await axios.delete(`${API}/ai-intel/prompts/${id}`);
    setTrackers((p) => p.filter((x) => x.id !== id));
    if (selected?.id === id) { setSelected(null); setLatest(null); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <div>
          <h2 className="text-xl font-black font-['Cabinet_Grotesk']">Prompt Tracker</h2>
          <p className="text-sm text-[var(--color-text-muted)]">Monitor specific AI prompts daily across 5 engines.</p>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="aii-prompts-add" className={btn + " text-sm"}><Plus className="w-4 h-4" /> New tracker</button>
      </div>

      {showForm && (
        <Modal onClose={() => setShowForm(false)} testid="aii-prompts-form">
          <form onSubmit={create}>
            <h3 className="text-lg font-black mb-4">Create a prompt tracker</h3>
            <label className="block text-sm text-[var(--color-text-muted)] mb-1">Brand name *</label>
            <input required value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })}
              placeholder="e.g. TopRankPulse" className={inputCls + " mb-3"} data-testid="aii-form-brand" />
            <label className="block text-sm text-[var(--color-text-muted)] mb-1">Prompts (one per line, max 10) *</label>
            <textarea required rows={6} value={form.promptsText} onChange={(e) => setForm({ ...form, promptsText: e.target.value })}
              placeholder={"best SEO tool for startups\ntop AI visibility trackers\n..."} className={inputCls + " mb-4 font-mono text-sm"} data-testid="aii-form-prompts" />
            <button type="submit" disabled={loading} className={btn + " w-full"} data-testid="aii-form-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              Create & run first check
            </button>
          </form>
        </Modal>
      )}

      {trackers.length > 0 && (
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {trackers.map((tr) => (
            <button key={tr.id} onClick={() => setSelected(tr)} data-testid={`aii-prompt-${tr.id}`}
              className={`px-4 py-2 border text-sm font-bold whitespace-nowrap ${selected?.id === tr.id ? "bg-[var(--color-surface)] border-[#a855f7]" : "border-[var(--color-border)] text-[var(--color-text-muted)]"}`}>
              {tr.brand} · {tr.prompts?.length || 0}
            </button>
          ))}
        </div>
      )}

      {!trackers.length && !showForm && (
        <EmptyState icon={Search} title="No trackers yet" sub="Add up to 10 prompts and track mentions across ChatGPT, Perplexity, Gemini, Claude, Copilot."
          cta="Create first tracker" onClick={() => setShowForm(true)} testid="aii-prompts-first" />
      )}

      {selected && latest && (
        <div className="space-y-5" data-testid="aii-prompts-results">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-black font-['Cabinet_Grotesk']">{selected.brand}</h3>
            <div className="flex gap-2">
              <button onClick={() => rerun(selected.id)} disabled={running} data-testid="aii-prompts-rerun"
                className="px-3 py-2 bg-[var(--color-surface)] border border-[var(--color-border)] text-xs font-bold flex items-center gap-1 hover:border-zinc-500">
                {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Re-run
              </button>
              <button onClick={() => remove(selected.id)} className="p-2 text-[var(--color-text-muted)] hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>

          {latest.summary && (
            <div className="grid md:grid-cols-3 gap-4">
              <Stat label="AI visibility" value={`${latest.summary.visibility_pct}%`} big />
              <Stat label="Total mentions" value={latest.summary.mentions_total} />
              <Stat label="Run at" value={new Date(latest.run_at).toLocaleString()} small />
            </div>
          )}

          <div className="space-y-3">
            {(latest.results || []).map((row, i) => (
              <div key={i} className="bg-[var(--color-surface)] border border-[var(--color-border)] p-4" data-testid={`aii-prompt-result-${i}`}>
                <p className="font-mono text-sm text-[#c084fc] mb-3">"{row.prompt}"</p>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  {Object.entries(row.engines || {}).map(([engine, e]) => (
                    <div key={engine} className={`p-2 border text-center text-xs ${e.mentioned ? "border-emerald-500/40 bg-emerald-500/5" : "border-[var(--color-border)]"}`}>
                      <div className="font-bold">{engine}</div>
                      <div className={`text-[10px] ${e.mentioned ? "text-emerald-400" : "text-[var(--color-text-muted)]"}`}>
                        {e.mentioned ? `#${e.rank || "—"}` : "not mentioned"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================================================================
 * 2) Citation Intelligence
 * ========================================================================= */
function CitationsTab({ email, language }) {
  const [form, setForm] = useState({ brand: "", website: "", topic: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async (e) => {
    e.preventDefault();
    if (!form.brand || !form.website || !form.topic) return;
    setLoading(true);
    const r = await axios.post(`${API}/ai-intel/citations/run`, { email, language, ...form });
    setResult(r.data);
    setLoading(false);
  };
  return (
    <div className="space-y-6" data-testid="aii-citations">
      <h2 className="text-xl font-black">Citation Intelligence</h2>
      <p className="text-sm text-[var(--color-text-muted)]">Discover which URLs AI engines cite when answering about your topic — and how many come from your domain.</p>
      <form onSubmit={run} className="bg-[var(--color-surface)] border border-[var(--color-border)] p-4 grid md:grid-cols-3 gap-3">
        <input required placeholder="Brand *" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className={inputCls} data-testid="aii-cit-brand" />
        <input required placeholder="Website URL *" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className={inputCls} data-testid="aii-cit-web" />
        <input required placeholder="Topic (e.g. ai seo tools) *" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} className={inputCls} data-testid="aii-cit-topic" />
        <button disabled={loading} className={btn + " md:col-span-3"} data-testid="aii-cit-run">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Run citation scan
        </button>
      </form>
      {result && (
        <div className="space-y-4" data-testid="aii-cit-result">
          <div className="grid md:grid-cols-3 gap-4">
            <Stat label="Total citations" value={result.summary?.total_citations ?? 0} />
            <Stat label="From your site" value={result.summary?.user_citations ?? 0} />
            <Stat label="Your share" value={`${result.summary?.user_share_pct ?? 0}%`} big />
          </div>
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] divide-y divide-[var(--color-border)]">
            {(result.citations || []).map((c, i) => (
              <div key={i} className="p-3 flex items-start gap-3" data-testid={`aii-cit-row-${i}`}>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${c.is_user ? "bg-emerald-500/15 text-emerald-400" : c.is_competitor ? "bg-red-500/15 text-red-400" : "bg-[var(--color-bg)] text-[var(--color-text-muted)]"}`}>
                  {c.engine}
                </span>
                <div className="flex-1 text-sm">
                  <div className="font-bold break-all">{c.url}</div>
                  <div className="text-xs text-[var(--color-text-muted)]">{c.snippet}</div>
                </div>
                <span className="text-xs font-mono text-[#c084fc]">{c.authority_score}/100</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================================================================
 * 3) Sentiment
 * ========================================================================= */
function SentimentTab({ email, language }) {
  const [form, setForm] = useState({ brand: "", topic: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async (e) => {
    e.preventDefault();
    if (!form.brand) return;
    setLoading(true);
    const r = await axios.post(`${API}/ai-intel/sentiment/run`, { email, language, ...form });
    setResult(r.data);
    setLoading(false);
  };
  return (
    <div className="space-y-6" data-testid="aii-sentiment">
      <h2 className="text-xl font-black">Sentiment Analysis</h2>
      <p className="text-sm text-[var(--color-text-muted)]">How positive or negative do AI engines sound when talking about your brand?</p>
      <form onSubmit={run} className="bg-[var(--color-surface)] border border-[var(--color-border)] p-4 grid md:grid-cols-3 gap-3">
        <input required placeholder="Brand *" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className={inputCls} data-testid="aii-sent-brand" />
        <input placeholder="Topic angle (optional)" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} className={inputCls} data-testid="aii-sent-topic" />
        <button disabled={loading} className={btn} data-testid="aii-sent-run">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Analyse
        </button>
      </form>
      {result && (
        <div className="space-y-4" data-testid="aii-sent-result">
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6 text-center">
            <p className="text-xs font-mono uppercase text-[var(--color-text-muted)] tracking-wider mb-2">Overall sentiment</p>
            <p className="text-5xl font-black font-mono" style={{ color: result.overall?.score > 0.3 ? "#22c55e" : result.overall?.score < -0.1 ? "#ef4444" : "#c084fc" }}>
              {(result.overall?.score ?? 0).toFixed(2)}
            </p>
            <p className="text-sm uppercase font-bold mt-1">{result.overall?.label || "neutral"}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {(result.engines || []).map((e, i) => (
              <div key={i} className="bg-[var(--color-surface)] border border-[var(--color-border)] p-4" data-testid={`aii-sent-engine-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold">{e.engine}</span>
                  <span className={`text-[10px] px-2 py-0.5 font-bold uppercase ${e.sentiment === "positive" ? "bg-emerald-500/15 text-emerald-400" : e.sentiment === "negative" ? "bg-red-500/15 text-red-400" : "bg-[var(--color-bg)] text-[var(--color-text-muted)]"}`}>{e.sentiment}</span>
                </div>
                <p className="text-sm text-[var(--color-text-muted)]">"{e.snippet}"</p>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <DriverList title="Positive drivers" items={result.positive_drivers} color="text-emerald-400" Icon={TrendingUp} />
            <DriverList title="Negative drivers" items={result.negative_drivers} color="text-red-400" Icon={TrendingDown} />
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================================================================
 * 4) Content Gap
 * ========================================================================= */
function GapsTab({ email, language }) {
  const [form, setForm] = useState({ brand: "", website: "", industry: "", competitors: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async (e) => {
    e.preventDefault();
    if (!form.brand || !form.industry) return;
    setLoading(true);
    const competitors = form.competitors.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 5);
    const r = await axios.post(`${API}/ai-intel/gap/run`, { email, language, ...form, competitors });
    setResult(r.data);
    setLoading(false);
  };
  const prio = { high: "bg-red-500/15 text-red-400", medium: "bg-amber-500/15 text-amber-400", low: "bg-emerald-500/15 text-emerald-400" };
  return (
    <div className="space-y-6" data-testid="aii-gaps">
      <h2 className="text-xl font-black">Content Gap Analyzer</h2>
      <p className="text-sm text-[var(--color-text-muted)]">Which topics do AI engines answer that you don't cover? Competitors already do.</p>
      <form onSubmit={run} className="bg-[var(--color-surface)] border border-[var(--color-border)] p-4 grid md:grid-cols-2 gap-3">
        <input required placeholder="Brand *" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className={inputCls} data-testid="aii-gap-brand" />
        <input placeholder="Website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className={inputCls} data-testid="aii-gap-web" />
        <input required placeholder="Industry / niche *" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className={inputCls} data-testid="aii-gap-industry" />
        <input placeholder="Competitor domains (comma-separated)" value={form.competitors} onChange={(e) => setForm({ ...form, competitors: e.target.value })} className={inputCls} data-testid="aii-gap-comps" />
        <button disabled={loading} className={btn + " md:col-span-2"} data-testid="aii-gap-run">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Find content gaps
        </button>
      </form>
      {result && (
        <div className="space-y-4" data-testid="aii-gap-result">
          <div className="grid md:grid-cols-3 gap-4">
            <Stat label="Your coverage" value={`${result.coverage?.user_pct ?? 0}%`} big />
            <Stat label="Competitor coverage" value={`${result.coverage?.competitor_pct ?? 0}%`} />
            <Stat label="Topics analysed" value={result.coverage?.industry_topics_total ?? 0} />
          </div>
          <div className="space-y-2">
            {(result.gaps || []).map((g, i) => (
              <div key={i} className="bg-[var(--color-surface)] border border-[var(--color-border)] p-4" data-testid={`aii-gap-row-${i}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${prio[g.priority] || prio.medium}`}>{g.priority}</span>
                  <span className="font-bold">{g.topic}</span>
                  <span className="ml-auto text-xs font-mono text-[var(--color-text-muted)]">{g.search_volume || 0}/mo</span>
                </div>
                <p className="text-sm text-[var(--color-text-muted)] mb-1">{g.action}</p>
                {g.competitor_covers?.length > 0 && (
                  <p className="text-[11px] text-[var(--color-text-muted)]">Already covered by: <span className="text-[#c084fc]">{g.competitor_covers.join(", ")}</span></p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------------------- small helpers ----------------------------- */
function Stat({ label, value, big, small }) {
  return (
    <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-4">
      <p className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-text-muted)] mb-1">{label}</p>
      <p className={`font-black font-mono ${big ? "text-4xl text-[#a855f7]" : small ? "text-sm" : "text-2xl"}`}>{value}</p>
    </div>
  );
}
function DriverList({ title, items, color, Icon }) {
  if (!items?.length) return null;
  return (
    <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-4">
      <p className={`text-xs font-bold font-mono uppercase tracking-wider mb-2 flex items-center gap-1 ${color}`}>
        <Icon className="w-3.5 h-3.5" /> {title}
      </p>
      <ul className="space-y-1 text-sm">
        {items.map((x, i) => <li key={i} className="flex gap-2"><span className="text-[var(--color-text-muted)]">•</span><span>{x}</span></li>)}
      </ul>
    </div>
  );
}
function Modal({ children, onClose, testid }) {
  return (
    <div className="fixed inset-0 bg-[var(--color-bg)]/80 z-50 flex items-center justify-center p-4" data-testid={testid}>
      <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6 max-w-lg w-full relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-[var(--color-text-muted)] hover:text-white"><X className="w-5 h-5" /></button>
        {children}
      </div>
    </div>
  );
}
function EmptyState({ icon: Icon, title, sub, cta, onClick, testid }) {
  return (
    <div className="text-center py-16">
      <Icon className="w-14 h-14 text-zinc-700 mx-auto mb-5" />
      <h2 className="text-xl font-black font-['Cabinet_Grotesk'] mb-2">{title}</h2>
      <p className="text-[var(--color-text-muted)] mb-6 max-w-md mx-auto text-sm">{sub}</p>
      <button onClick={onClick} data-testid={testid} className={btn + " mx-auto"}><Plus className="w-4 h-4" /> {cta}</button>
    </div>
  );
}
